import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Activity, User, Clock } from "lucide-react";

const ActivityLog = () => {
  const { user, authLoading } = useApp();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    document.title = "Activity Log — Dream Home Plans";
    if (user && (user.role === "super_admin" || user.role === "admin")) {
      api.get("/admin/activity-log", { params: { limit: 300 } })
        .then((r) => setLogs(r.data.items || r.data || []))
        .catch(() => setLogs([]))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/activity-log/export.csv", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = "activity-log.csv"; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { /* ignore */ if (typeof console !== "undefined") console.debug("[silent]", e); }
  };

  if (authLoading) return <Layout><div className="p-24 text-center">Loading…</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin" && user.role !== "admin") {
    return <Layout><div className="p-24 text-center text-muted-foreground">Access denied.</div></Layout>;
  }

  const filtered = filter
    ? logs.filter((l) => (l.action || "").includes(filter) || (l.user_email || "").includes(filter))
    : logs;

  const actionColor = (action) => {
    if (action.startsWith("plan.")) return "text-primary";
    if (action.startsWith("user.")) return "text-accent";
    if (action.startsWith("auth.")) return "text-muted-foreground";
    if (action.startsWith("role.")) return "text-accent";
    return "text-foreground";
  };

  return (
    <Layout title="Activity Log">
      <div className="max-w-[1400px] mx-auto">
        <div className="overline text-accent">Administration</div>
        <p className="mt-2 text-muted-foreground text-sm">Every important action performed by every user, with before/after values where applicable.</p>

        <div className="mt-6 flex flex-wrap gap-2 items-center">
          <Input placeholder="Filter by action or email…" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-none h-11 max-w-md" data-testid="activity-filter" />
          <button data-testid="activity-export-csv" onClick={exportCsv} className="h-11 px-4 border border-border hover:border-accent text-sm">Export CSV</button>
        </div>

        <div className="mt-8 space-y-3" data-testid="activity-list">
          {loading && <div className="text-muted-foreground text-center py-16">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-muted-foreground text-center py-16 border border-border">
              <Activity size={30} className="mx-auto mb-3 text-accent" />
              No activity yet.
            </div>
          )}
          {!loading && filtered.map((l) => (
            <div key={l.log_id} className="border border-border p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-testid={`log-${l.log_id}`}>
              <div className="md:col-span-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Clock size={12} />
                {new Date(l.timestamp).toLocaleString()}
              </div>
              <div className="md:col-span-3 flex items-center gap-2 text-sm">
                <User size={14} className="text-accent shrink-0" />
                <div className="min-w-0">
                  <div className="truncate">{l.user_email || "anonymous"}</div>
                  {l.user_role && <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l.user_role}</div>}
                </div>
              </div>
              <div className="md:col-span-4">
                <div className={`font-mono text-sm ${actionColor(l.action)}`}>{l.action}</div>
                {l.entity_id && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {l.entity_type}: <span className="font-mono">{l.entity_id}</span>
                  </div>
                )}
                {(l.before || l.after) && (
                  <details className="mt-2 text-xs text-muted-foreground">
                    <summary className="cursor-pointer hover:text-accent">before / after</summary>
                    <pre className="mt-2 bg-muted p-2 overflow-x-auto text-[10px]">{JSON.stringify({ before: l.before, after: l.after }, null, 2)}</pre>
                  </details>
                )}
              </div>
              <div className="md:col-span-2 text-xs text-muted-foreground font-mono truncate">
                {l.ip || "—"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default ActivityLog;
